import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { publicAPI } from '../api';

const ConfirmReceipt: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'confirming' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  const handleConfirmation = async () => {
    if (!token) return;

    setStatus('confirming');

    try {
      await publicAPI.confirmDocument(token);
      setStatus('success');
      setMessage('Thank you! Your document receipt has been confirmed. The document has been automatically deleted from our system for your privacy.');

      // Redirect after success
      setTimeout(() => {
        window.close(); // Close the window if opened from email
      }, 3000);

    } catch (error: any) {
      setStatus('error');
      if (error.response?.status === 400) {
        setMessage('This confirmation link has already been used or is invalid.');
      } else if (error.response?.status === 404) {
        setMessage('Document not found. It may have already been processed.');
      } else {
        setMessage('Failed to confirm document receipt. Please try again or contact the shop.');
      }
    }
  };

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid confirmation link. Please check the link in your email or SMS.');
      return;
    }

    // Auto-confirm after showing document info
    const timer = setTimeout(() => {
      handleConfirmation();
    }, 5000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleManualConfirm = () => {
    handleConfirmation();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            {status === 'loading' && <Clock className="w-8 h-8 text-blue-600" />}
            {status === 'confirming' && <Clock className="w-8 h-8 text-blue-600 animate-spin" />}
            {status === 'success' && <CheckCircle className="w-8 h-8 text-green-600" />}
            {status === 'error' && <AlertCircle className="w-8 h-8 text-red-600" />}
          </div>
          
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {status === 'loading' && 'Preparing Confirmation...'}
            {status === 'confirming' && 'Confirming Receipt...'}
            {status === 'success' && 'Receipt Confirmed!'}
            {status === 'error' && 'Confirmation Failed'}
          </h1>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {status === 'loading' && (
            <div className="text-center">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Loading confirmation details...
              </p>
              <p className="text-xs text-gray-400 mt-2">
                This will automatically confirm in 5 seconds
              </p>
            </div>
          )}

          {status === 'confirming' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-sm text-gray-600">
                Processing your confirmation...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium mb-2">
                  ✅ Confirmation Successful
                </p>
                <p className="text-xs text-green-700">
                  Your document has been confirmed as received and will be automatically 
                  deleted from our system to protect your privacy.
                </p>
              </div>
              
              <div className="text-xs text-gray-500">
                This window will close automatically in a few seconds.
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ❌ Confirmation Failed
                </p>
                <p className="text-xs text-red-700">
                  {message}
                </p>
              </div>

              {token && (
                <button
                  onClick={handleManualConfirm}
                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Try Again
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-2">
              Powered by Prindox
            </p>
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
              <span>🔒 Secure</span>
              <span>🗑️ Auto-Delete</span>
              <span>📱 Mobile-First</span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-6 max-w-md text-center">
        <p className="text-xs text-gray-500">
          This confirmation ensures your document was successfully printed and received. 
          The document will be permanently deleted from our system for your privacy and security.
        </p>
      </div>
    </div>
  );
};

export default ConfirmReceipt;